import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  submit,
  SET_TANF_SUBMISSION_STATUS,
  getTanfSubmissionStatus,
  getAvailableFileList,
} from '../actions/reports'
import { useEventLogger } from '../utils/eventLogger'
import { useFormSubmission } from './useFormSubmission'
import { POLLING_TIMEOUT_MESSAGE } from '../components/Reports/constants'
import { useReportsContext } from '../components/Reports/ReportsContext'

/**
 * Custom hook that encapsulates shared file upload form logic
 * Used by both QuarterFileUploadForm and SectionFileUploadForm
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.stt - STT object with id
 * @param {Function} config.transformFiles - Function to transform uploaded files before submission
 * @param {Function} config.formatSections - Function to format section names for display
 * @param {Function} config.getSubmitPayload - Function to generate submit action payload
 * @returns {Object} Form state and handlers
 */
export const useFileUploadForm = ({
  stt,
  transformFiles,
  formatSections,
  getSubmitPayload,
}) => {
  const dispatch = useDispatch()
  const logger = useEventLogger()

  const {
    yearInputValue,
    quarterInputValue,
    fileTypeInputValue,
    uploadAlert,
    setUploadAlertState,
    processingAlert,
    setProcessingAlertState,
    uploadAlertRef,
    processingAlertRef,
    uploadedFiles,
    setErrorModalVisible,
    setModalTriggerSource,
    handleClearFilesOnly,
    handleClearAll,
    handleOpenFeedbackWidget,
    startPolling,
  } = useReportsContext()

  const user = useSelector((state) => state.auth.user)
  const { isSubmitting, executeSubmission } = useFormSubmission()

  // Format sections for display in success message
  const formattedSections = formatSections(uploadedFiles)

  const onFileUploadSuccess = (fileIds) => {
    const pollSubmissionStatus = () =>
      fileIds.forEach((fileId) => {
        startPolling(
          `${fileId}`,
          () => getTanfSubmissionStatus(fileId),
          (response) => {
            let summary = response?.data?.summary
            return summary && summary.status && summary.status !== 'Pending'
          },
          (response) => {
            dispatch({
              type: SET_TANF_SUBMISSION_STATUS,
              payload: {
                datafile_id: fileId,
                datafile: response?.data,
              },
            })
          },
          (error) => {
            setProcessingAlertState({
              active: true,
              type: error.type ? error.type : 'error',
              message: error.message,
            })
          },
          (onError) => {
            onError({
              message: POLLING_TIMEOUT_MESSAGE,
              type: 'warning',
            })
          }
        )
      })

    dispatch(
      getAvailableFileList(
        {
          quarter: quarterInputValue,
          year: yearInputValue,
          stt: stt,
          file_type: fileTypeInputValue,
        },
        () => pollSubmissionStatus()
      )
    )

    // Clear the local upload panel after a successful submission; history and
    // polling continue via Redux state updates.
    handleClearFilesOnly()
  }

  // Handle form submission
  const onSubmit = async (event) => {
    event.preventDefault()

    const validationErrorCount = uploadedFiles.filter(
      (file) =>
        file.error ||
        (file.validatedYear && file.validatedYear !== yearInputValue) ||
        (fileTypeInputValue !== 'program-integrity-audit' &&
          quarterInputValue &&
          file.validatedQuarter &&
          file.validatedQuarter !== quarterInputValue)
    ).length

    if (uploadedFiles.length === 0) {
      setUploadAlertState({
        active: true,
        type: 'error',
        message:
          'No data files have been selected. Select at least one file before submitting.',
      })
      return
    }

    if (validationErrorCount > 0) {
      setUploadAlertState({
        active: true,
        type: 'error',
        message:
          validationErrorCount === 1
            ? 'There is 1 error that must be resolved before submitting'
            : `There are ${validationErrorCount} errors that must be resolved before submitting`,
      })
      return
    }

    try {
      // Transform files if needed (e.g., for Program Audit)
      const filesToSubmit = transformFiles
        ? transformFiles(uploadedFiles)
        : uploadedFiles

      // Get submit payload with all necessary parameters
      const payload = getSubmitPayload({
        quarter: quarterInputValue,
        year: yearInputValue,
        formattedSections,
        logger,
        setUploadAlertState,
        setProcessingAlertState,
        stt: stt?.id,
        uploadedFiles: filesToSubmit,
        user,
        fileType: fileTypeInputValue,
      })

      const submittedFiles = await executeSubmission(() =>
        dispatch(submit(payload, onFileUploadSuccess))
      )
      handleOpenFeedbackWidget(submittedFiles)
    } catch (error) {
      console.error('Error during form submission:', error)
      setUploadAlertState({
        active: true,
        type: 'error',
        message: 'An error occurred during submission. Please try again.',
      })
    }
  }

  // Handle cancel button click
  const handleCancel = () => {
    if (uploadedFiles.length > 0) {
      setModalTriggerSource('cancel')
      setErrorModalVisible(true)
    } else {
      handleClearAll()
    }
  }

  return {
    // Form state
    yearInputValue,
    quarterInputValue,
    fileTypeInputValue,
    uploadAlert,
    processingAlert,
    uploadedFiles,
    isSubmitting,
    uploadAlertRef,
    processingAlertRef,
    formattedSections,

    // Form handlers
    onSubmit,
    handleCancel,

    // Context setters (for FileUpload components)
    setUploadAlertState,
    setProcessingAlertState,
  }
}
